import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline';
import { InventoryService } from '../services/inventoryService';
import { InventoryItem, InventoryStats, InventoryFilters, ItemCondition, UpdateInventoryItemRequest, CreateSaleRequest } from '../types/inventory';
import { useToast } from '../hooks/useToast';
import LoadingSpinner from '../components/LoadingSpinner';
import { ItemCard, InventoryValueDisplay } from '../components/inventory';
import InventoryItemDialog from '../components/inventory/InventoryItemDialog';

interface InventoryPageProps {}

const InventoryPage: React.FC<InventoryPageProps> = () => {
  const [items, setItems] = useState<InventoryItem[]>([]);
  const [stats, setStats] = useState<InventoryStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState<InventoryFilters>({});
  const [showFilters, setShowFilters] = useState(false);
  const [activeDialog, setActiveDialog] = useState<{ item: InventoryItem; mode: 'view' | 'edit' | 'sold' } | null>(null);
  const [saving, setSaving] = useState(false);
  const { showToast } = useToast();

  useEffect(() => {
    loadInventory();
  }, [filters]);

  const loadInventory = async () => {
    try {
      setLoading(true);
      const [itemsData, statsData] = await Promise.all([
        InventoryService.getUserItems(filters),
        InventoryService.getInventoryStats()
      ]);

      setItems(itemsData);
      setStats(statsData);
    } catch (error) {
      console.error('Error loading inventory:', error);
      showToast('error', 'Failed to load inventory');
    } finally {
      setLoading(false);
    }
  };

  const filteredItems = items.filter(item =>
    item.item_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.item_description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleConditionFilter = (condition: ItemCondition | 'all') => {
    setFilters(prev => ({
      ...prev,
      condition: condition === 'all' ? undefined : condition
    }));
  };

  const formatCondition = (condition: ItemCondition) => {
    return condition.replace('_', ' ').toUpperCase();
  };

  const handleSaveItem = async (updates: UpdateInventoryItemRequest) => {
    if (!activeDialog || saving) return;
    try {
      setSaving(true);
      await InventoryService.updateItem(activeDialog.item.id, updates);
      await loadInventory();
      setActiveDialog(null);
      showToast('success', 'Inventory item updated');
    } catch (error) {
      console.error('Error updating inventory item:', error);
      showToast('error', 'Could not update item', error instanceof Error ? error.message : 'Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleMarkSold = async (sale: CreateSaleRequest) => {
    if (!activeDialog || saving) return;
    try {
      setSaving(true);
      await InventoryService.createSale(sale);
      await loadInventory();
      setActiveDialog(null);
      showToast('success', 'Item marked as sold');
    } catch (error) {
      console.error('Error marking inventory item as sold:', error);
      showToast('error', 'Could not mark item as sold', error instanceof Error ? error.message : 'Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-96">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Inventory</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Manage your items and track their performance
          </p>
        </div>
        <Link
          to="/item-listing"
          className="inline-flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
        >
          <PlusIcon className="h-5 w-5" />
          Add New Item
        </Link>
      </div>

      {/* Stats Cards */}
      {stats && (
        <InventoryValueDisplay stats={stats} />
      )}

      {/* Search and Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Search */}
          <div className="flex-1 relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search items..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
            />
          </div>

          {/* Filter Toggle */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            <FunnelIcon className="h-5 w-5" />
            Filters
          </button>
        </div>

        {/* Filter Options */}
        {showFilters && (
          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-600">
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => handleConditionFilter('all')}
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  !filters.condition
                    ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                    : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                }`}
              >
                All Conditions
              </button>
              {(['excellent', 'very_good', 'good', 'fair', 'poor'] as ItemCondition[]).map(condition => (
                <button
                  key={condition}
                  onClick={() => handleConditionFilter(condition)}
                  className={`px-3 py-1 rounded-full text-sm font-medium ${
                    filters.condition === condition
                      ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                      : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                  }`}
                >
                  {formatCondition(condition)}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Items Grid */}
      {filteredItems.length === 0 ? (
        <div className="text-center py-12">
          <div className="text-gray-400 dark:text-gray-600 mb-4">
            <ChartBarIcon className="h-16 w-16 mx-auto" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            {items.length === 0 ? 'No items yet' : 'No items match your search'}
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            {items.length === 0
              ? 'Start by adding your first item to the inventory.'
              : 'Try adjusting your search or filters.'
            }
          </p>
          {items.length === 0 && (
            <Link
              to="/item-listing"
              className="inline-flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
            >
              <PlusIcon className="h-5 w-5" />
              Add Your First Item
            </Link>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredItems.map(item => (
            <ItemCard
              key={item.id}
              item={item}
              onView={(itemToView) => setActiveDialog({ item: itemToView, mode: 'view' })}
              onEdit={(itemToEdit) => setActiveDialog({ item: itemToEdit, mode: 'edit' })}
              onMarkSold={(itemToSell) => setActiveDialog({ item: itemToSell, mode: 'sold' })}
            />
          ))}
        </div>
      )}
      {activeDialog && (
        <InventoryItemDialog
          key={`${activeDialog.item.id}-${activeDialog.mode}`}
          item={activeDialog.item}
          mode={activeDialog.mode}
          busy={saving}
          onClose={() => !saving && setActiveDialog(null)}
          onSave={handleSaveItem}
          onMarkSold={handleMarkSold}
        />
      )}
    </div>
  );
};

export default InventoryPage;
